import { useEffect, useRef, useState } from 'react';
import { line, Size, isEmailAddress, priorAskLine, t, type RfqLineChoice, type RfqPriorAsk, type RfqSendRequest, type RfqSentAnswer, type RfqVendorRow, type SuggestionRow, documentName } from '@bms2/shared';
import { Button, Checkbox, DatePicker, Dropdown, NumericInput, Select, TextInput, Textarea } from '../../components/controls';
import { AlertBanner, bannerIcon } from '../../components/controls/AlertBanner';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/modals/Modal';
import { ModalField } from '../../components/modals/ModalShell';
import modalStyles from '../../components/modals/modal.module.css';
import { apiFetch, post, useRows, DEALS_PATH } from './api';
import styles from './RfqModal.module.css';
import { useAddNew } from './AddNewHost';
import { reportCaughtInBrowser } from '../../shell/systemErrors';

export interface RfqModalProps {
  open: boolean;
  dealId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSent: (answer: RfqSentAnswer) => void;
}

const TYPED = 'typed';
const NO_LINES: readonly RfqLineChoice[] = [];
const NO_ASKS: readonly RfqPriorAsk[] = [];

/** The lines ticked when a vendor is picked: the ones suggested for it, or every line when nothing suggests any (piece 23). */
export function defaultTicks(lines: readonly RfqLineChoice[], vendor: RfqVendorRow | null): Set<string> {
  const suggested = vendor?.suggestedLineIds.filter((id) => lines.some((l) => l.bomLineId === id)) ?? [];
  return new Set(suggested.length > 0 ? suggested : lines.map((l) => l.bomLineId));
}

/**
 * Request for quotation from the Bill of materials tile (piece 23; decision
 * 0316): the vendor picked by hand, the lines ticked, the contact with a work
 * email or a typed address, the due date and a message; Send RFQ issues it
 * and emails the PDF and the sheet.
 */
export function RfqModal({ open, dealId, apiBaseUrl = '', onCancel, onSent }: RfqModalProps) {
  const addNew = useAddNew(apiBaseUrl);
  const lines = useRows<RfqLineChoice>(open ? `${DEALS_PATH}/${dealId}/rfq/lines` : null, apiBaseUrl);
  // The server's own array, stable across renders, so the tick effect below runs when the lines or the vendor change and not on every render.
  const rows = lines.state.kind === 'ready' ? lines.state.data : NO_LINES;
  const vendors = useRef(new Map<string, RfqVendorRow>());
  const [vendor, setVendor] = useState<RfqVendorRow | null>(null);
  const [vendorName, setVendorName] = useState('');
  const [contact, setContact] = useState<string>('');
  const [email, setEmail] = useState('');
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [dueOn, setDueOn] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [chaseEvery, setChaseEvery] = useState<number | null>(null);
  const [alarmAfter, setAlarmAfter] = useState<number | null>(null);
  // The ticked lines no vendor covers (piece 22): asked of the server as the ticks change, and acknowledged before the send goes.
  const tickedKey = [...ticked].sort().join(',');
  // The answer is kept beside the ticks it was asked for, so a stale answer reads as nothing and an acknowledgement lapses when the ticks change.
  const [fetchedUnassigned, setFetchedUnassigned] = useState<{ key: string; rows: readonly RfqLineChoice[] }>({ key: '', rows: [] });
  const [acknowledgedKey, setAcknowledgedKey] = useState<string | null>(null);
  const unassigned = fetchedUnassigned.key === tickedKey ? fetchedUnassigned.rows : [];
  const acknowledged = acknowledgedKey === tickedKey;
  useEffect(() => {
    if (!open || tickedKey === '') return undefined;
    const controller = new AbortController();
    apiFetch(`${apiBaseUrl}${DEALS_PATH}/${dealId}/rfq/unassigned?lines=${encodeURIComponent(tickedKey)}`, { signal: controller.signal })
      .then(async (response) => (response.ok ? ((await response.json()) as { rows: RfqLineChoice[] }).rows : []))
      .then((rows) => { if (!controller.signal.aborted) setFetchedUnassigned({ key: tickedKey, rows }); })
      .catch((failed: unknown) => {
        reportCaughtInBrowser('records.rfq.unassignedLines', failed);
      });
    return () => controller.abort();
  }, [open, dealId, apiBaseUrl, tickedKey]);

  // 🔨 The requests this vendor was already sent for these lines (decision 0499): asked of the server as the vendor or the ticks change, and confirmed before the send goes.
  const repeatKey = vendor ? `${vendor.companyId}|${tickedKey}` : '';
  // Kept beside the vendor and the ticks it was asked for, so a stale answer reads as nothing and a confirmation lapses when either moves.
  const [fetchedRepeat, setFetchedRepeat] = useState<{ key: string; rows: readonly RfqPriorAsk[] }>({ key: '', rows: NO_ASKS });
  const [confirmedRepeatKey, setConfirmedRepeatKey] = useState<string | null>(null);
  const priorAsks = fetchedRepeat.key === repeatKey ? fetchedRepeat.rows : NO_ASKS;
  const confirmedRepeat = confirmedRepeatKey === repeatKey;
  useEffect(() => {
    if (!open || repeatKey === '' || vendor === null) return undefined;
    const controller = new AbortController();
    apiFetch(`${apiBaseUrl}${DEALS_PATH}/${dealId}/rfq/repeat?vendor=${encodeURIComponent(vendor.companyId)}&lines=${encodeURIComponent(tickedKey)}`, { signal: controller.signal })
      .then(async (response) => (response.ok ? ((await response.json()) as { rows: RfqPriorAsk[] }).rows : []))
      .then((rows) => { if (!controller.signal.aborted) setFetchedRepeat({ key: repeatKey, rows }); })
      .catch((failed: unknown) => {
        reportCaughtInBrowser('records.rfq.priorAsks', failed);
      });
    return () => controller.abort();
  }, [open, dealId, apiBaseUrl, repeatKey, tickedKey, vendor]);

  // The lines arriving after the modal opened re-tick the list; the state adjusts during the render that sees them.
  const [seenRows, setSeenRows] = useState(rows);
  if (seenRows !== rows) {
    setSeenRows(rows);
    setTicked(defaultTicks(rows, vendor));
  }

  /** Picking a vendor re-ticks the lines suggested for it and points the send at its Sales mailbox, else its first contact. */
  const pick = (row: SuggestionRow) => {
    const chosen = vendors.current.get(row.id) ?? null;
    setVendor(chosen);
    setVendorName(row.line1);
    setTicked(defaultTicks(rows, chosen));
    // 🔒 Decision 2974: the vendor's open Sales mailbox goes ahead of its first contact, typed in and changeable.
    if (chosen?.salesMailbox) {
      setContact(TYPED);
      setEmail(chosen.salesMailbox);
      return;
    }
    setContact(chosen?.contacts[0]?.employmentId ?? TYPED);
  };

  const search = async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${DEALS_PATH}/${dealId}/rfq/vendors?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: RfqVendorRow[] };
    return body.rows.map((row) => {
      vendors.current.set(row.companyId, row);
      const suggested = row.suggestedLineIds.length;
      return { id: row.companyId, line1: row.displayName, line2: suggested > 0 ? line(String(suggested), t('deal.rfq.suggested').toLowerCase()) : '' };
    });
  };

  const toggle = (id: string, on: boolean) =>
    setTicked((was) => {
      const next = new Set(was);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const send = async () => {
    if (!vendor) return setError(t('deal.rfq.needsVendor'));
    if (ticked.size === 0) return setError(t('deal.rfq.noLines'));
    const typed = contact === TYPED;
    if (typed && !isEmailAddress(email)) return setError(t('deal.rfq.badAddress'));
    // 🔨 The server refuses the issue over an unacknowledged unassigned line; the modal says so first rather than sending to be told.
    if (unassigned.length > 0 && !acknowledged) return setError(t('deal.rfq.needsAcknowledgement'));
    // 🔨 The server refuses a repeat of lines this vendor was already asked about; the modal asks first rather than sending to be told.
    if (priorAsks.length > 0 && !confirmedRepeat) return setError(t('deal.rfq.needsRepeatConfirmation'));
    const body: RfqSendRequest = {
      vendorCompanyId: vendor.companyId,
      contactEmploymentId: typed ? null : contact,
      email: typed ? email.trim() : null,
      bomLineIds: rows.filter((l) => ticked.has(l.bomLineId)).map((l) => l.bomLineId),
      responsesDueOn: dueOn || null,
      message: message.trim() || null,
      ...(unassigned.length > 0 ? { acknowledgeUnassigned: acknowledged } : {}),
      ...(priorAsks.length > 0 ? { confirmRepeat: confirmedRepeat } : {}),
      chaseEveryWorkingDays: chaseEvery,
      noResponseAlarmWorkingDays: alarmAfter,
    };
    setBusy(true);
    const answer = await post<RfqSentAnswer>(`${DEALS_PATH}/${dealId}/rfq`, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    onSent(answer.data);
  };

  return (
    <Modal
      open={open}
      title={documentName('RFQ')}
      onCancel={onCancel}
      dataModal="rfq"
      width="wide"
      actions={[
        { kind: 'primary', label: t('deal.rfq.send'), onAct: () => void send(), disabled: busy || !vendor || ticked.size === 0 || (unassigned.length > 0 && !acknowledged) || (priorAsks.length > 0 && !confirmedRepeat) },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {lines.state.kind === 'ready' && rows.length === 0 ? <p className={modalStyles.fieldLabel} data-rfq-empty="true">{t('deal.rfq.emptyBom')}</p> : null}
      <div className={modalStyles.pair}>
        <div data-rfq-field="vendor">
          <Dropdown size={Size.Medium} label={t('deal.rfq.vendor')} entityLabel={t('deal.rfq.vendorEntity')} search={search} minChars={0} value={vendorName} onSelect={pick} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'vendor' }, pick)} required />
        </div>
        <div>
          <Select size={Size.Large} label={t('deal.rfq.contact')} required value={contact} onChange={setContact} disabled={!vendor} options={[...(vendor?.contacts ?? []).map((row) => ({ value: row.employmentId, label: line(row.name, '·', row.email) })), { value: TYPED, label: t('deal.rfq.typedAddress') }]} dataAttribute={{ 'data-rfq-field': 'contact' }} />
          {vendor && vendor.contacts.length === 0 ? <span className={modalStyles.fieldLabel} data-rfq-no-contacts="true">{t('deal.rfq.noContacts')}</span> : null}
        </div>
      </div>
      {contact === TYPED ? <TextInput size={Size.Medium} label={t('deal.rfq.email')} value={email} onChange={setEmail} type="email" required name="rfqEmail" /> : null}
      <ModalField label={t('deal.rfq.lines')} required>
        <div className={styles.tickBar}>
          <span className={styles.count} data-rfq-ticked={ticked.size}>{line(String(ticked.size), '/', String(rows.length))}</span>
          <Button size={Size.Small} text={[t('deal.rfq.tickAll')]} actions={[() => setTicked(new Set(rows.map((l) => l.bomLineId)))]} disabled={rows.length === 0} />
          <Button size={Size.Small} text={[t('deal.rfq.tickNone')]} actions={[() => setTicked(new Set())]} disabled={ticked.size === 0} />
        </div>
        <ul className={styles.lines} data-rfq-lines={rows.length}>
          {rows.map((row) => (
            <li key={row.bomLineId} className={styles.line} data-rfq-line={row.bomLineId} data-rfq-suggested={vendor?.suggestedLineIds.includes(row.bomLineId) ? 'true' : 'false'}>
              <Checkbox
                label={line(row.sku, row.manufacturerPartNumber ? `(${row.manufacturerPartNumber})` : null, '·', row.itemName, '·', String(row.quantity), row.unitName)}
                checked={ticked.has(row.bomLineId)}
                size={Size.Small}
                onChange={(on) => toggle(row.bomLineId, on)}
              />
              {row.description ? <span className={styles.description}>{row.description}</span> : null}
            </li>
          ))}
        </ul>
      </ModalField>
      {priorAsks.length > 0 ? (
        <div className={styles.repeat} data-rfq-repeat={priorAsks.length}>
          <p className={`${modalStyles.fieldLabel} ${styles.heading}`}>
            <span className={styles.headingIcon} data-rfq-icon={bannerIcon.warning}><Icon meaning={bannerIcon.warning} size="inline" /></span>
            {t('deal.rfq.repeat')}
          </p>
          <ul className={styles.lines}>
            {priorAsks.map((ask) => (
              <li key={ask.rfqNumber} className={styles.line} data-rfq-repeat-rfq={ask.rfqNumber}>{priorAskLine(ask)}</li>
            ))}
          </ul>
          <span className={styles.description}>{t('deal.rfq.repeatHint')}</span>
          <Checkbox label={t('deal.rfq.confirmRepeat')} checked={confirmedRepeat} onChange={(on) => setConfirmedRepeatKey(on ? repeatKey : null)} size={Size.Small} name="confirmRepeat" />
        </div>
      ) : null}
      {unassigned.length > 0 ? (
        <div className={styles.unassigned} data-rfq-unassigned={unassigned.length}>
          <p className={`${modalStyles.fieldLabel} ${styles.heading}`}>
            <span className={styles.headingIcon} data-rfq-icon={bannerIcon.warning}><Icon meaning={bannerIcon.warning} size="inline" /></span>
            {t('deal.rfq.unassigned')}
          </p>
          <ul className={styles.lines}>
            {unassigned.map((row) => (
              <li key={row.bomLineId} className={styles.line} data-rfq-unassigned-line={row.bomLineId}>{line(row.sku, '·', row.itemName, '·', String(row.quantity), row.unitName)}</li>
            ))}
          </ul>
          <span className={styles.description}>{t('deal.rfq.unassignedHint')}</span>
          <Checkbox label={t('deal.rfq.acknowledge')} checked={acknowledged} onChange={(on) => setAcknowledgedKey(on ? tickedKey : null)} size={Size.Small} name="acknowledgeUnassigned" />
        </div>
      ) : null}
      <div className={modalStyles.pair}>
        <DatePicker size={Size.Small} label={t('deal.rfq.dueOn')} value={dueOn} onChange={setDueOn} mode="date" name="rfqDueOn" />
      </div>
      <div className={modalStyles.pair}>
        <NumericInput size={Size.Small} label={t('deal.rfq.chaseEvery')} kind="number" decimals={0} value={chaseEvery} onChange={setChaseEvery} name="chaseEveryWorkingDays" />
        <NumericInput size={Size.Small} label={t('deal.rfq.alarmAfter')} kind="number" decimals={0} value={alarmAfter} onChange={setAlarmAfter} name="noResponseAlarmWorkingDays" />
      </div>
      <p className={modalStyles.fieldLabel}>{t('deal.rfq.chaseHint')}</p>
      <Textarea size={Size.Large} label={t('deal.rfq.message')} value={message} onChange={setMessage} />
      <p className={modalStyles.fieldLabel} data-rfq-attachments="true">{t('deal.rfq.attachments')}</p>
      {addNew.host}
    </Modal>
  );
}
